(ns app.front.process.save-tracking
  "Registry of in-flight save operations, keyed by invocation.

  The active operations themselves live in the shared `core.front.operation-registry`, the same
  store monitored command operations use; this namespace keeps only the save-cycle results under
  its own key:

    ::results {workspace-id :saved | :failed}

  An operation's metadata is {:signal-id :process.signal/… | nil
                              :workspace-id \"abc-123\" | nil
                              :track-save? true|false
                              :chained?    true|false}. Operations are stored flat and queried by
  scope, so settling an operation is a lookup by id: a response that lands after the user
  navigated away still settles under the workspace the operation was started in, instead of
  leaking or polluting the workspace they moved to. Because the shared registry also holds
  operations that are not workspace-scoped (commands), workspace selection requires the metadata
  to carry `:workspace-id`; external saves with a nil workspace still carry the key and keep
  working.

  This registry backs two different questions. `app.front.process` asks \"is a signal of this
  kind in flight?\" for button spinners, over every operation of the current workspace.
  The wizard save indicator asks \"how is the current save cycle doing?\", over the operations
  that opted into `:track-save?` — a save cycle spans every tracked operation that overlaps in
  flight, starting one while nothing is pending clears the previous result, and a failure sticks
  so a later success cannot report :saved over it. Completions for unknown operation ids are
  ignored, which makes callbacks arriving after `::cleanup` harmless.

  Tracking is gated twice: callers opt in per call with `:track-save?` in the `::process/signal`
  opts, and the current route opts in via `:track-saves? true` in its route data. Shared
  components can therefore ask to be tracked unconditionally without knowing whether they happen
  to run inside a tracking context (e.g. the wizard), while the calls that must never drive the
  indicator (submitting, exiting the wizard) simply do not ask."
  (:require [core.front.operation-registry :as operation-registry]
            [re-frame.core-instrumented :as rf]))

(defn tracking-enabled?
  "True when the current route opts into save tracking via `:track-saves? true`."
  [db]
  (get-in db [:route/match :data :track-saves?]))

(defn current-workspace-id [db]
  (or (get-in db [:route/match :parameters :path :workspace-id])
      (get-in db [:route/match :parameters :query :workspace-id])))

(defn- operations-in [db workspace-id]
  (->> (operation-registry/active-operations db)
       vals
       (filter #(and (contains? % :workspace-id)
                     (= workspace-id (:workspace-id %))))))

(defn- tracked-pending? [db workspace-id]
  (boolean (some :track-save? (operations-in db workspace-id))))

(defn processing-signal-ids
  "The kinds of signal currently in flight in `workspace-id` — the busy set behind the
   button spinners."
  [db workspace-id]
  (into #{} (keep :signal-id) (operations-in db workspace-id)))

(defn start-operation
  "Registers `operation-id` as in flight."
  [db operation-id {:keys [signal-id workspace-id track-save? chained?]}]
  (if-let [operation (operation-registry/operation db operation-id)]
    ;; A chained follow-up re-enters with the same id: it takes over the operation with its own
    ;; signal kind and its own answer to \"is another request still owed?\", without opening a new
    ;; cycle. The scope and the tracking decision stay those of the request that opened it.
    (operation-registry/start-operation db operation-id
                                        (assoc operation
                                               :signal-id signal-id
                                               :chained?  (boolean chained?)))
    (cond-> (operation-registry/start-operation db operation-id
                                                {:signal-id    signal-id
                                                 :workspace-id workspace-id
                                                 :track-save?  (boolean track-save?)
                                                 :chained?     (boolean chained?)})
      ;; The operation that opens a cycle clears the result the previous one settled on.
      (and track-save? (not (tracked-pending? db workspace-id)))
      (update ::results dissoc workspace-id))))

(defn succeed-operation [db operation-id]
  (if-let [{:keys [workspace-id track-save? chained?]} (operation-registry/operation db operation-id)]
    (if chained?
      ;; A follow-up request still owes this operation, so a 200 here must not settle it (see
      ;; `attachments/confirm-attachments` and `application-wizard.core/autosave-location`).
      db
      (let [db (operation-registry/settle-operation db operation-id)]
        ;; Only the operation that empties the cycle settles it, and never over an earlier
        ;; failure — one failed save must not be masked by its siblings succeeding.
        (cond-> db
          (and track-save?
               (not (tracked-pending? db workspace-id))
               (not= :failed (get-in db [::results workspace-id])))
          (assoc-in [::results workspace-id] :saved))))
    db))

(defn fail-operation [db operation-id]
  (if-let [{:keys [workspace-id track-save?]} (operation-registry/operation db operation-id)]
    (cond-> (operation-registry/settle-operation db operation-id)
      ;; Recorded immediately, even with siblings still pending: the status stays :saving until
      ;; the cycle ends, so this only becomes visible then, but it can no longer be overwritten.
      track-save? (assoc-in [::results workspace-id] :failed))
    db))

(defn state->status [db workspace-id]
  (if (tracked-pending? db workspace-id)
    :saving
    (or (get-in db [::results workspace-id]) :idle)))

(defn hide-settled-state [db workspace-id]
  (cond-> db
    (not (tracked-pending? db workspace-id)) (update ::results dissoc workspace-id)))

(defn- tracked-save [db operation-id]
  ;; The non-signal saves (workspace files, party submit chain) are tracked saves by definition,
  ;; and have no signal kind to report as busy.
  (start-operation db operation-id {:workspace-id (current-workspace-id db)
                                    :track-save?  true}))

(rf/reg-event-db ::save-started
  (fn [db [_ operation-id]]
    (tracked-save db operation-id)))

(rf/reg-event-db ::save-succeeded
  (fn [db [_ operation-id]]
    (succeed-operation db operation-id)))

(rf/reg-event-db ::save-failed
  (fn [db [_ operation-id]]
    (fail-operation db operation-id)))

;; Records a save that completed outside the registry (the caller owns the request), so the
;; indicator settles on :saved. Routed through a throwaway operation-id rather than assoc-ing
;; the result directly, so it still respects a cycle that is mid-flight or already failed.
(rf/reg-event-db ::record-external-save
  (fn [db _]
    (let [operation-id (random-uuid)]
      (-> db
          (tracked-save operation-id)
          (succeed-operation operation-id)))))

(rf/reg-event-db ::step-entered
  (fn [db _]
    (hide-settled-state db (current-workspace-id db))))

;; Only the wizard workspace's result is cleared: the registry itself is shared with the button
;; spinners, and the wizard exit that dispatches this is followed by a route change, so the
;; workspace is passed in rather than read off the route.
(rf/reg-event-db ::cleanup
  (fn [db [_ workspace-id]]
    (update db ::results dissoc workspace-id)))

(rf/reg-sub ::status
  (fn [db _]
    (state->status db (current-workspace-id db))))
